import {BlockSkeleton, InlineSkeleton} from '@pinecast/common/Skeleton';
import type {DonationPage_podcast} from '@pinecast/gql/__generated_tj__/DonationPage';
import type {DonationSuccessPage_podcast} from '@pinecast/gql/__generated_tj__/DonationSuccessPage';
import {FormattedMessage} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';
import * as React from 'react';

type Props = {
  podcast: DonationPage_podcast | DonationSuccessPage_podcast | null;
  slug: string;
};

const DonationPageHeader = ({podcast, slug}: Props) => {
  const css = useCSS();
  const designColors = useColors();

  return (
    <div
      className={css({
        alignItems: 'flex-start',
        display: 'flex',
        paddingBottom: '32px',
        paddingTop: '24px',
      })}
    >
      {podcast ? (
        <img
          src={podcast.coverArtThumbnail}
          alt=""
          aria-hidden="true"
          className={css({
            borderRadius: '4px',
            marginRight: '8px',
            border: `1px solid ${designColors.borderGrayNormal}`,
          })}
          height={64}
          width={64}
        />
      ) : (
        <BlockSkeleton
          style={{height: '64px', width: '64px', marginRight: '8px'}}
        />
      )}
      <div>
        <h1
          className={css({
            margin: 0,
            fontSize: '24px',
            fontWeight: 500,
            lineHeight: '28px',
          })}
        >
          {podcast ? (
            <FormattedMessage
              description="Title of the donation page. {podcastName} is the name of the podcast"
              defaultMessage="Support {podcastName}"
              values={{podcastName: podcast.name}}
            />
          ) : (
            <InlineSkeleton style={{maxWidth: '300px'}} width="300px" />
          )}
        </h1>
        <div
          className={css({
            color: designColors.headingBlue,
            fontSize: '14px',
            fontWeight: 500,
            marginTop: '4px',
          })}
        >
          {podcast?.hasPrivateEpisodes ? (
            <FormattedMessage
              description="Note that the podcast has private episodes"
              defaultMessage="Tip this show to access its private episodes and support great podcasting."
            />
          ) : (
            <FormattedMessage
              description="Generic message encouraging you to donate"
              defaultMessage="Contributing to your favorite podcasts helps them grow and improve."
            />
          )}
        </div>
        {!podcast && (
          <div className={css({fontSize: '14px', marginTop: '4px'})}>
            <InlineSkeleton width="150px" />
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(DonationPageHeader);
